import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { countries } from '../utils/countries';
import './SearchZone.css';

const SearchZone = ({ onBack }) => {
    const [destCountrySearch, setDestCountrySearch] = useState('');
    const [showCountryResults, setShowCountryResults] = useState(false);
    const [destCountry, setDestCountry] = useState(null);
    const [postalCode, setPostalCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const filteredCountries = useMemo(() => {
        if (!destCountrySearch) return [];
        return countries.filter(c =>
            c.name.toLowerCase().includes(destCountrySearch.toLowerCase()) ||
            c.code.toLowerCase().includes(destCountrySearch.toLowerCase())
        ).slice(0, 10);
    }, [destCountrySearch]);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!destCountry) {
            setError('Please select a country');
            return;
        }

        setError(null);
        setLoading(true);
        setResult(null);

        try {
            const apiUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api/check-remote-area' : '/api/check-remote-area';
            const res = await axios.post(apiUrl, {
                country: destCountry.code,
                postalCode: postalCode
            });

            if (res.data.success) {
                setResult(res.data);
            } else {
                setError(res.data.message || 'Failed to check zone');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error connecting to service');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="search-zone-container">
            <div className="search-zone-header">
                <button className="back-btn" onClick={onBack}>←</button>
                <h2>🌍 UPS Search Zone Module</h2>
            </div>

            <div className="search-zone-content">
                <div className="search-form-card">
                    <form onSubmit={handleSearch}>
                        <div className="form-group country-search-container">
                            <label>Country</label>
                            <input
                                type="text"
                                placeholder="Search country..."
                                value={destCountry ? destCountry.name : destCountrySearch}
                                onChange={(e) => {
                                    setDestCountrySearch(e.target.value);
                                    if (destCountry) setDestCountry(null);
                                    setShowCountryResults(true);
                                }}
                                onFocus={() => setShowCountryResults(true)}
                                required
                            />
                            {showCountryResults && filteredCountries.length > 0 && (
                                <div className="country-search-results">
                                    {filteredCountries.map(c => (
                                        <div
                                            key={c.code}
                                            className="country-option"
                                            onClick={() => {
                                                setDestCountry(c);
                                                setShowCountryResults(false);
                                                setDestCountrySearch(c.name);
                                            }}
                                        >
                                            {c.name} ({c.code})
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <label>Postal Code</label>
                            <input
                                type="text"
                                placeholder="e.g. 10001"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                                required
                            />
                        </div>

                        <button type="submit" className="search-btn" disabled={loading}>
                            {loading ? 'Searching...' : 'Fetch Zone & Check Area'}
                        </button>
                    </form>
                </div>

                <div className="search-result-card">
                    {loading ? (
                        <div className="result-loading">
                            <div className="spinner"></div>
                            <p>Querying UPS for remote area data...</p>
                        </div>
                    ) : error ? (
                        <div className="result-error">
                            <div className="error-icon">❌</div>
                            <p>{error}</p>
                        </div>
                    ) : result ? (
                        <div className="result-data">
                            <div className={`status-banner ${result.isRemote ? 'remote' : 'normal'}`}>
                                <span className="status-icon">{result.isRemote ? '⚠️' : '✅'}</span>
                                <div className="status-text">
                                    <h3>{result.message}</h3>
                                    <p>{result.isRemote ? 'Surcharges will apply' : 'Standard delivery rates apply'}</p>
                                </div>
                            </div>

                            <div className="charge-details">
                                <div className="detail-row">
                                    <label>UPS Shipping Zone:</label>
                                    <span style={{ color: '#805ad5' }}>Zone {result.zone}</span>
                                </div>

                                <div className="detail-row">
                                    <label>Delivery Surcharge (Code 190):</label>
                                    <span className={parseFloat(result.charge190) > 0 ? "has-charge" : ""}>
                                        {result.charge190} {result.currency}
                                    </span>
                                </div>

                                {result.details && result.details.length > 0 && (
                                    <div className="all-charges">
                                        <h4>Breakdown:</h4>
                                        {result.details.map((d, i) => (
                                            <div key={i} className="detail-item">
                                                <span>Code {d.Code}</span>
                                                <span>{d.MonetaryValue} {result.currency}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="total-row">
                                    <label>Total Surcharge Amount:</label>
                                    <span>{result.totalSurcharge} {result.currency}</span>
                                </div>
                            </div>

                            {result.alerts && result.alerts.length > 0 && (
                                <div className="result-alerts">
                                    <h4>System Alerts:</h4>
                                    {result.alerts.map((a, i) => (
                                        <p key={i}>• {a.description}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="result-placeholder">
                            <div className="placeholder-icon">🌍</div>
                            <p>Enter Country and Postal Code to fetch the UPS Shipping Zone and check if it's considered a Remote or Extended delivery area.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchZone;
